import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, useContext, useCallback, useRef, useMemo, act } from 'react'
import {
  Menu, User, Users, GraduationCap, Book, ClipboardList, ChevronLast, Crown, Building, Building2, BookOpenCheck, Library,
  LayoutDashboard, LineChart, Repeat, Check, CreditCard, UserCheck
} from 'lucide-react'
import { motion } from 'framer-motion'
import { LayoutContext } from './KuizyLayout'
import { getMembershipsCached } from '@/api/userCache'
import { selectOrg, fetchMemberships } from '@/api/auth'
import { getPendingApprovalsCount } from '@/api/kuizy'

export default function Sidebar({ drawer = false, onClose }) {
  const { mode, roles, toggleMode } = useContext(LayoutContext)
  const nav = useNavigate()
  const location = useLocation()

  const isMasterAdminRole = roles.includes('MASTER_ADMIN')
  const isAdminRole = isMasterAdminRole || roles.includes('ADMIN')
  const [collapsed, setCollapsed] = useState(!drawer && window.innerWidth < 768)

  const [memberships, setMemberships] = useState([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)
  const [orgSearch, setOrgSearch] = useState('')

  const [activeOrgName, setActiveOrgName] = useState('')
  const [activeOrgId, setActiveOrgId] = useState('');

  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const [tempOrgId, setTempOrgId] = useState('');

  const [pendingCount, setPendingCount] = useState(0)

  const listRef = useRef(null)

  useEffect(() => {
    if (drawer) return
    const onResize = () => {
      if (window.innerWidth < 768 && !collapsed) setCollapsed(true)
      if (window.innerWidth >= 768 && collapsed) setCollapsed(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [collapsed, drawer])


  const loadMemberships = async ({
    pageNumber = 1,
    search = orgSearch,
    reset = false,
  } = {}) => {

    if (loading && !reset) return

    try {
      setLoading(true)

      const { data } = await fetchMemberships({
        page: pageNumber,
        per_page: 10,
        search,
      })

      const newItems = data?.items || []

      // ✅ ใช้ active_org จาก backend โดยตรง
      if (data?.active_org) {
        const active = data.active_org

        setActiveOrgId(active.organization_id)
        setActiveOrgName(active.name_en || active.name_th)

        localStorage.setItem('orgId', active.organization_id)
      }

      setMemberships(prev => {
        if (reset) return newItems

        const map = new Map()
        prev.forEach(i => map.set(i.organization_id, i))
        newItems.forEach(i => map.set(i.organization_id, i))
        return Array.from(map.values())
      })

      setPage(data?.page || 1)
      setTotalPages(data?.total_pages || 1)

    } catch (err) {
      console.error("Failed to load memberships:", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMemberships({ reset: true })

    const fetchCount = () => {
      getPendingApprovalsCount()
        .then(({ data }) => {
          const count = data?.count || 0
          setPendingCount(count)

          document.title = count > 0
            ? `(${count}) Kuizy`
            : 'Kuizy'
        })
        .catch(err => {
          console.error('Failed to fetch pending approvals count:', err)
        })
    }

    fetchCount()

    window.addEventListener("approval-updated", fetchCount)

    return () => {
      window.removeEventListener("approval-updated", fetchCount)
    }
  }, [])

  useEffect(() => {
    if (orgModalOpen) {
      loadMemberships({ pageNumber: 1, reset: true })
    }
  }, [orgModalOpen])

  useEffect(() => {
    if (!orgModalOpen) return
    const el = listRef.current
    if (!el) return

    const handleScroll = () => {
      if (
        el.scrollTop + el.clientHeight >= el.scrollHeight - 40
      ) {
        if (!loading && page < totalPages) {
          loadMemberships({ pageNumber: page + 1 })
        }
      }
    }

    el.addEventListener("scroll", handleScroll)
    return () => el.removeEventListener("scroll", handleScroll)
  }, [orgModalOpen, loading, page, totalPages])

  const handleConfirmOrg = async () => {
    if (!tempOrgId) return

    try {
      const { data } = await selectOrg(tempOrgId)

      if (data?.active_org_id) {
        localStorage.setItem('orgId', data.active_org_id)
      }

      if (data?.roles) {
        localStorage.setItem('roles', JSON.stringify(data.roles))
      }

      setOrgModalOpen(false)
      window.location.href = '/dashboard'

    } catch (err) {
      console.error('selectOrg failed', err)
    }
  }

  const closeIfDrawer = useCallback(() => {
    if (typeof onClose === 'function') onClose()
  }, [onClose])

  const waitFrame = () => new Promise(res => requestAnimationFrame(res))
  const ensureModeThenNavigate = useCallback(
    async (targetMode, to) => {
      localStorage.setItem('viewMode', targetMode)
      if (mode !== targetMode) toggleMode()
      for (let i = 0; i < 10; i++) {
        await waitFrame()
        const ls = localStorage.getItem('viewMode')
        if (ls === targetMode) break
      }
      nav(to, { replace: false })
      closeIfDrawer()
    },
    [mode, toggleMode, nav, closeIfDrawer]
  )

  const adminMenus = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'แดชบอร์ด' },
    {
      to: '/course', icon: Library, label: 'จัดการหลักสูตร',
      badge: pendingCount > 0 ? pendingCount : null
    },
    { to: '/course/approvals', icon: ClipboardList, label: 'การอนุมัติ' },
    // { to: '/group', icon: Users, label: 'จัดการกลุ่มผู้เรียน' },
    { to: '/learners', icon: User, label: 'จัดการผู้เรียน' },
    ...(isMasterAdminRole
      ? [
        { to: '/approver', icon: UserCheck, label: 'จัดการผู้อนุมัติ' },
        { to: '/role', icon: Crown, label: 'จัดการผู้ใช้งานระบบ' },
        { to: '/organization', icon: Building, label: 'จัดการข้อมูลองค์กร' },
        { to: '/subscription', icon: CreditCard, label: 'จัดการแผนการใช้งาน' }
      ]
      : [{ to: '/role', icon: Crown, label: 'จัดการผู้ใช้งานระบบ' }]),
  ]

  const creatorMenus = [
    { to: '/class', icon: GraduationCap, label: 'จัดการห้องเรียน' },
    { to: '/group', icon: Users, label: 'จัดการกลุ่มผู้เรียน' },
    { to: '/my-courses', icon: Library, label: 'จัดการหลักสูตร' },
    { to: '/content', icon: Book, label: 'จัดการเนื้อหา' },
    { to: '/tests', icon: ClipboardList, label: 'จัดการชุดข้อสอบ' },
    { to: '/player', icon: BookOpenCheck, label: 'จัดการเกมข้อสอบ(ผู้เล่น)' },
  ]

  const creatorReportMenus = [
    { to: '/learners/reports-results', icon: LineChart, label: 'รายงานผลการสอบ(ผู้เรียน)' },
    { to: '/learners/report', icon: User, label: 'รายงานรายบุคคล' },
  ]

  const activeRoute = [...adminMenus, ...creatorMenus, ...creatorReportMenus]
    .map(m => m.to)
    .reduce((acc, curr) => {
      if (location.pathname === curr || location.pathname.startsWith(curr + '/')) {
        if (!acc || curr.length > acc.length) {
          return curr;
        }
      }
      return acc;
    }, null);

  const buildRenderQueue = hasAdmin => {
    const queue = []
    if (hasAdmin) {
      queue.push({ kind: 'title', text: 'Admin', icon: Crown })
      adminMenus.forEach(it => queue.push({ ...it, targetMode: 'admin', kind: 'item', variant: 'admin' }))
      queue.push({ kind: 'divider' })
    }
    queue.push({ kind: 'title', text: 'Creator', icon: Library })
    creatorMenus.forEach(it => queue.push({ ...it, targetMode: hasAdmin ? 'admin' : 'user', kind: 'item', variant: 'creator' }))
    const hasReports = creatorReportMenus.length > 0
    if (hasReports) {
      queue.push({ kind: 'divider' })
      queue.push({ kind: 'title', text: 'Creator · Reports', icon: LineChart })
      creatorReportMenus.forEach(it => queue.push({ ...it, targetMode: hasAdmin ? 'admin' : 'user', kind: 'item', variant: 'creator' }))
    }
    const btnIdx = queue.map((q, i) => (q.kind === 'item' ? i : -1)).filter(i => i >= 0)
    const firstIdx = btnIdx[0]
    const lastIdx = btnIdx[btnIdx.length - 1]
    return queue.map((q, i) =>
      q.kind === 'item' ? { ...q, first: i === firstIdx, last: i === lastIdx } : q
    )
  }

  const GroupTitle = ({ text, icon: Icon }) => (
    <div className={`${collapsed && !drawer ? 'px-0' : 'px-3'} py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500/80 flex items-center gap-1.5`}>
      {(!collapsed || drawer) && (
        <>
          <Icon size={12} className="shrink-0" />
          <span>{text}</span>
        </>
      )}
    </div>
  )

  const Item = ({ icon: Icon, label, to, targetMode, first, last, badge, variant }) => {
    const isActive = to === activeRoute
    const [hover, setHover] = useState(false)
    const [delta, setDelta] = useState(0)
    const containerRef = useRef(null)
    const textRef = useRef(null)
    const handleEnter = () => {
      const c = containerRef.current
      const t = textRef.current
      if (!c || !t) return
      const extra = t.scrollWidth - c.clientWidth
      if (extra > 4) {
        setDelta(extra)
        setHover(true)
      }
    }

    const handleLeave = () => setHover(false)
    const duration = Math.max(3, delta / 50)
    return (
      <button
        onClick={() => ensureModeThenNavigate(targetMode, to)}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className={`group w-full text-left ${collapsed ? 'px-2' : 'px-3'} ${variant === 'admin' ? 'text-pink-600' : 'text-sky-600'}  py-2.5 text-sm font-medium hover:bg-black/5 flex items-center gap-3 text-gray-700 ${first ? 'rounded-t-2xl' : ''} ${last ? 'rounded-b-2xl' : ''} ${isActive ? 'bg-black/10' : ''}`}
        title={label}
      >
        <Icon className="shrink-0" size={18} />
        {!(!drawer && collapsed) && (
          <>
            <div ref={containerRef} className="flex-1 min-w-0 overflow-hidden">
              <motion.span
                ref={textRef}
                className="block whitespace-nowrap"
                animate={hover ? { x: -delta } : { x: 0 }}
                transition={
                  hover
                    ? { duration, repeat: Infinity, repeatType: 'reverse', ease: 'linear' }
                    : { duration: 0.2 }
                }
              >
                {label}
              </motion.span>
            </div>

            {badge && (
              <span className="ml-2 min-w-[20px] h-5 px-1.5 text-xs flex items-center justify-center rounded-full bg-red-500 text-white">
                {badge > 99 ? '99+' : badge}
              </span>
            )}
          </>
        )}
      </button>
    )
  }

  if (drawer) {
    const hasAdmin = isAdminRole && adminMenus.length > 0
    const renderQueue = buildRenderQueue(hasAdmin)
    return (
      <aside className="w-[84vw] max-w-[300px] h-full bg-white flex flex-col">
        <div className="flex-1 overflow-y-auto p-2">
          <div className="rounded-2xl bg-[linear-gradient(to_bottom,rgba(239,68,68,0.04),rgba(16,185,129,0.04))]">
            {renderQueue.map((q, idx) =>
              q.kind === 'divider' ? (
                <div key={`div-${idx}`} className="mx-3 my-1 h-px bg-black/10 rounded-full" />
              ) : q.kind === 'title' ? (
                <GroupTitle key={`title-${idx}`} text={q.text} icon={q.icon} />
              ) : (
                <Item
                  key={`${q.targetMode}-${q.to}`}
                  icon={q.icon}
                  label={q.label}
                  to={q.to}
                  targetMode={q.targetMode}
                  first={q.first}
                  last={q.last}
                  badge={q.badge}
                  variant={q.variant}
                />

              )
            )}
          </div>
        </div>
        <div className="p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
          <button
            onClick={closeIfDrawer}
            className="w-full py-2.5 rounded-xl bg-gray-100 text-gray-700 font-medium"
          >
            ปิดเมนู
          </button>
        </div>
      </aside>
    )
  }

  const hasAdmin = isAdminRole && adminMenus.length > 0
  const renderQueue = buildRenderQueue(hasAdmin)

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 60 : 240 }}
      className="h-screen bg-white border-r border-gray-200 flex flex-col shrink-0"
    >
      <div
        onClick={() => nav('/dashboard', { replace: true })}
        className="h-16 flex items-center justify-center cursor-pointer border-b border-gray-200 px-2"
      >
        {collapsed ? (
          <Menu className="text-primary" />
        ) : (
          <div
            role="img"
            aria-label="Kuizy"
            className="w-full h-12 bg-center bg-contain bg-no-repeat"
            style={{ backgroundImage: 'url(/images/app-logo.png)' }}
          />
        )}
      </div>

      <div className={`p-2 pt-2 ${collapsed ? 'px-2' : ''}`}>
        {collapsed ? (
          <div className="flex items-center justify-center bg-gray-50 rounded-lg">
            <button
              type="button"
              onClick={() => { setTempOrgId(activeOrgId); setOrgModalOpen(true); }}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-md"
              title="เปลี่ยนองค์กร"
            >
              <Building className="w-6 h-6" />
            </button>
          </div>
        ) : (
          <div className="px-4 py-3 rounded-xl border border-gray-200  bg-gray-100 shadow-sm">
            <div className="flex items-center justify-between ">
              <div className="flex gap-3">
                <Building className="w-5 h-5 text-gray-500" />
                <div className="text-sm text-gray-500 mb-1">องค์กร</div>
              </div>
              <button
                type="button"
                onClick={() => { setTempOrgId(activeOrgId); setOrgModalOpen(true); }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <Repeat className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm font-medium text-gray-800">
              {/* {
                memberships.find(m => m.organization_id === activeOrgId)?.name_en
                || memberships.find(m => m.organization_id === activeOrgId)?.name_th
                || ' '
              } */}
              {activeOrgName || ' '}
            </div>
          </div>
        )}
      </div>

      {orgModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOrgModalOpen(false)} />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-lg p-4 z-10">
            <h3 className="text-lg font-semibold mb-3">เลือกองค์กร</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="search"
                value={orgSearch}
                onChange={e => setOrgSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    loadMemberships({
                      pageNumber: 1,
                      search: orgSearch,
                      reset: true,
                    })
                  }
                }}
                className="flex-1 px-3 py-2 border rounded-md text-sm"
              />
              <button
                onClick={() =>
                  loadMemberships({
                    pageNumber: 1,
                    search: orgSearch,
                    reset: true,
                  })
                }
                className="px-3 py-2 bg-primary text-white rounded-md text-sm"
              >
                ค้นหา
              </button>
            </div>
            <div ref={listRef} className="max-h-64 overflow-y-auto space-y-3">
              {memberships.map((m) => {
                const active = tempOrgId === m.organization_id
                return (
                  <button
                    key={m.organization_id}
                    type="button"
                    onClick={() => setTempOrgId(m.organization_id)}
                    className={[
                      "w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition",
                      "text-left cursor-pointer",
                      active
                        ? "bg-pink-50 border-pink-500 shadow-[0_0_0_3px_rgba(236,72,153,0.15)]"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    ].join(" ")}
                  >
                    {/* ไอคอนอาคารด้านซ้าย */}
                    <div
                      className={[
                        "h-10 w-10 rounded-xl flex items-center justify-center",
                        active ? "bg-pink-500 text-white" : "bg-pink-100 text-pink-600"
                      ].join(" ")}
                    >
                      <Building2 size={24} />
                    </div>

                    {/* ชื่อองค์กร */}
                    <div className="flex-1">
                      <div className="text-base font-medium">{m.name_th || m.name_en}</div>
                      {m.name_en && <div className="text-sm text-gray-500">{m.name_en}</div>}
                    </div>

                    {/* วงกลมติ๊กถูกด้านขวา */}
                    <div
                      className={[
                        "h-6 w-6 rounded-full border flex items-center justify-center",
                        active ? "bg-pink-500 border-pink-500" : "bg-white border-gray-300"
                      ].join(" ")}
                    >
                      {active && (
                        <Check size={16} color="#ffffff" />
                      )}
                    </div>

                    {/* radio ที่ซ่อน ไว้เพื่อฟอร์ม */}
                    <input
                      type="radio"
                      name="org-radio"
                      value={m.organization_id}
                      checked={active}
                      onChange={() => setTempOrgId(m.organization_id)}
                      className="sr-only"
                    />
                  </button>
                )
              })}

              {loading && (
                <div className="text-sm text-gray-500 text-center py-3">
                  กำลังโหลด...
                </div>
              )}

              {!loading && page >= totalPages && memberships.length > 0 && (
                <div className="text-xs text-gray-400 text-center py-2">
                  แสดงครบแล้ว
                </div>
              )}

              {!loading && memberships.length === 0 && (
                <div className="text-sm text-gray-500">
                  ไม่พบองค์กรที่ค้นหา
                </div>
              )}

            </div>


            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setOrgModalOpen(false)} className="py-2 px-3 rounded-md bg-gray-100 cursor-pointer">ยกเลิก</button>
              <button
                type="button"
                onClick={async () => {
                  if (!tempOrgId) return

                  try {
                    const { data: org } = await selectOrg(tempOrgId)

                    if (org?.active_org_id) {
                      localStorage.setItem('orgId', org.active_org_id)
                      setActiveOrgId(org.active_org_id)

                      // ✅ อัปเดตชื่อ org ตรงนี้เลย
                      const selected = memberships.find(
                        m => m.organization_id === org.active_org_id
                      )

                      if (selected) {
                        setActiveOrgName(
                          selected.name_en || selected.name_th
                        )
                      }
                    }

                    if (org?.roles) {
                      localStorage.setItem('roles', JSON.stringify(org.roles))
                    }

                    setOrgModalOpen(false)
                    setOrgSearch('')

                    window.location.href = '/dashboard'

                  } catch (err) {
                    console.error('selectOrg failed', err)
                  }
                }}

                className="py-2 px-4 rounded-md bg-primary text-white cursor-pointer"
              >
                ยืนยัน
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-3">
        <div className="rounded-2xl bg-[linear-gradient(to_bottom,rgba(239,68,68,0.04),rgba(16,185,129,0.04))]">
          {renderQueue.map((q, idx) =>
            q.kind === 'divider' ? (
              <div key={`div-${idx}`} className="mx-3 my-1 h-px bg-black/10 rounded-full" />
            ) : q.kind === 'title' ? (
              <GroupTitle key={`title-${idx}`} text={q.text} icon={q.icon} />
            ) : (
              <Item
                key={`${q.targetMode}-${q.to}`}
                icon={q.icon}
                label={q.label}
                to={q.to}
                targetMode={q.targetMode}
                first={q.first}
                last={q.last}
                badge={q.badge}
                variant={q.variant}
              />
            )
          )}
        </div>
      </div>

      <button
        onClick={() => setCollapsed(!collapsed)}
        className="h-10 flex items-center justify-center border-t border-gray-200 text-gray-600 hover:bg-black/5"
        title={collapsed ? 'ขยายแถบเมนู' : 'ยุบแถบเมนู'}
      >
        <ChevronLast size={18} />
      </button>
    </motion.aside>
  )
}
